import { AccountStatementResponse, CompetitionsResponse } from '@/types';
import { apiSlice } from './apiSlice';
import {
  GameTransactionsParams,
  GameTransactionsResponse,
  TransactionsParams,
  TransactionsResponse,
} from '@/types/accountStatement';

export const commonApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    addMember: builder.mutation<
      any,
      {
        firstname?: string;
        lastname?: string;
        username?: string;
        mobile?: string;
        roleId: number;
        password: string;
        transactionCode?: string;
        fromAccount?: string;
        partnership?: number;
        platformId?: string;
      }
    >({
      query: (body) => ({
        url: '/users/sub-user',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Bankers', 'Members'],
    }),
    membersChangePassword: builder.mutation<
      any,
      {
        id: string;
        newPassword: string;
      }
    >({
      query: ({ id, newPassword }) => ({
        url: `/admin/users/password/${id}`,
        method: 'PATCH',
        body: { newPassword },
      }),
    }),
    uploadFile: builder.mutation<{ url: string; meta: any }, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: '/upload',
          method: 'POST',
          body: formData,
        };
      },
    }),
    getAccountStatement: builder.query<
      AccountStatementResponse,
      {
        page?: number;
        limit?: number;
        search?: string;
        context?: string;
        fromDate?: string;
        toDate?: string;
        userId?: number;
        recordType?: string;
        platformId?: string;
        sort?: string;
        direction?: 'asc' | 'desc';
      }
    >({
      query: ({
        search,
        fromDate,
        toDate,
        page,
        limit,
        userId,
        context,
        recordType,
        platformId,
        sort,
        direction,
      }) => ({
        url: '/users/transactions/all',
        method: 'GET',
        params: {
          ...(search ? { search } : {}),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ...(page ? { page } : {}),
          ...(context ? { context } : {}),
          ...(limit ? { limit } : {}),
          ...(userId ? { userId } : {}),
          ...(recordType ? { recordType } : {}),
          ...(platformId ? { platformId } : {}),
          ...(sort ? { sort } : {}),
          ...(direction ? { direction } : {}),
          ts: Date.now(),
        },
      }),
    }),
    //deposit -withdraw
    deposit: builder.mutation<
      any,
      {
        amount: number;
        fromAccount: string;
        toAccount: string;
        userId: string;
        transactionCode: string;
      }
    >({
      query: ({ userId, ...body }) => ({
        url: `users/${userId}/wallets/deposit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BalanceSummaryDataApi', 'Bankers', 'Members', 'Wallet'],
    }),
    withdraw: builder.mutation<
      any,
      {
        amount: number;
        fromAccount: string;
        toAccount: string;
        userId: string;
        transactionCode: string;
      }
    >({
      query: ({ userId, ...body }) => ({
        url: `users/${userId}/wallets/withdraw`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Bankers', 'Members', 'Wallet'],
    }),

    getCompetitions: builder.query<CompetitionsResponse, { sport: string }>({
      query: ({ sport }) => ({
        url: '/competitions',
        method: 'GET',
        params: {
          sport,
          ts: Date.now(),
        },
      }),
    }),
    getMyTransactions: builder.query<TransactionsResponse, TransactionsParams | void>({
      query: (params) => {
        const p = params || {};
        const queryParams: Record<string, string | number> = {};

        if (p.page !== undefined) queryParams.page = p.page;
        if (p.limit !== undefined) queryParams.limit = p.limit;
        if (p.search) queryParams.search = p.search;
        if (p.fromDate) queryParams.fromDate = p.fromDate;
        if (p.toDate) queryParams.toDate = p.toDate;
        if (p.context) queryParams.context = p.context;
        if (p.type) queryParams.type = p.type;
        if (p.walletType) queryParams.walletType = p.walletType;
        if (p.recordType) queryParams.recordType = p.recordType;
        if (p.platformId) queryParams.platformId = p.platformId;
        if (p.sort) queryParams.sort = p.sort;
        if (p.direction) queryParams.direction = p.direction;

        return {
          url: '/users/me/transactions',
          method: 'GET',
          params: { t: Date.now(), ...queryParams },
        };
      },
    }),

    getGameTransactions: builder.query<GameTransactionsResponse, GameTransactionsParams>({
      query: (params) => ({
        url: '/users/transactions/game-transactions',
        method: 'GET',
        params,
      }),
      // providesTags: ['GameTransactions'],
    }),
    addMemberRole: builder.mutation<
      any,
      {
        firstname: string;
        lastname?: string;
        email: string;
        username?: string;
        mobile?: string;
        roleId: number;
        userType?: string;
        password: string;
        transactionCode?: string;
        fromAccount?: string;
        partnership?: number;
        isRole?: number;
      }
    >({
      query: (body) => ({
        url: '/privileges/sub-user/role/admin',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Members', 'accessMember'],
    }),
  }),
});

export const {
  useAddMemberMutation,
  useMembersChangePasswordMutation,
  useUploadFileMutation,
  useGetAccountStatementQuery,
  useDepositMutation,
  useWithdrawMutation,
  useGetCompetitionsQuery,
  useGetMyTransactionsQuery,
  useGetGameTransactionsQuery,
  useAddMemberRoleMutation,
} = commonApi;
